import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Form, Tooltip } from "antd";
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes } from 'utils/formUtils';
import Vocabulary from 'components/Vocabulary';
import ProductAPI from 'restAPI/ProductAPI';
import SubProductAPI from 'restAPI/SubProductAPI';
import BaseModalForm from 'components/BaseModalForm';
import message from 'components/CustomMessage';
import { CloseIcon } from 'components/Icons';
import { Environments } from 'views/Findings/Constants';
import useEnvironmentVariables from "hooks/useEnvironmentVariables"
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';

const ALL_PRODUCTS_CONFIG = {
    id: 'All Products',
    name: <>All <Vocabulary name="product" isPlural={true}/></>
};

const ALL_SUBPRODUCTS_CONFIG = {
    id: 'All Subproducts',
    name: <>All <Vocabulary name="subProduct" isPlural={true}/></>
};

export const ChannelHelperText = () => {
    const  REACT_APP_ARMORCODE_SLACK_APP = useEnvironmentVariables('REACT_APP_ARMORCODE_SLACK_APP');
 return   <ul>
    <li>Please enter a channel name as per your <Vocabulary name="team" isLowerCase={true} />/workspace.</li>
    <li>The <a href={`https://armorcode.slack.com/apps/${REACT_APP_ARMORCODE_SLACK_APP}`} rel="noopener noreferrer" target="_blank">ArmorCode Slack App</a> needs to be added to the private channel before it can be configured here.</li>
</ul>
}
export default function SlackIntegrationForm({ config, isEdit, onUpdateConfigComponent, workspaceId, afterClose }) {
    const [channels, setChannels] = useState();
    const [form] = Form.useForm();
    const thisRef = useRef();

    const [selectedProduct, setSelectedProduct] = useState(config?.apId);
    const [productList, setProductList] = useState(null);
    const [subProductList, setSubProductList] = useState(null);
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const saveConfig = useCallback(async (values) => {
        let channelsData = {}
        values.channelId.forEach(id => {
            channelsData[id] =  _.find(channels, { id: id })?.name || id
        });
            const data = {
                workspaceId: workspaceId,
                apId: values.apId === ALL_PRODUCTS_CONFIG.id ? null: values.apId,
                apName: _.find(productList, { id: values.apId })?.name,
                aspId: values.aspId === ALL_SUBPRODUCTS_CONFIG.id ? null: values.aspId,
                aspName: _.find(subProductList, { id: values.aspId })?.name,
                aeName: hasDisabledEnvironment ? envValue : values.aeId,
                enabled: true,
                channels: channelsData,
                type: 'OTHERS',
            }
            const newData  = _.omitBy(data, (item) => item === undefined || item === null)
            if(isEdit){
                await ToolsIntegrationsAPI.updateSlackIntegrationDetails(newData, config?.id).promise;
                message.success("Successfully Updated Slack Mapping")
            }
            else{
                await ToolsIntegrationsAPI.createSlackIntegration(newData, workspaceId).promise;
                message.success("Successfully Added Slack Mapping");
            }
            onUpdateConfigComponent && onUpdateConfigComponent();
            
    }, [config, channels, isEdit, onUpdateConfigComponent, productList, subProductList, workspaceId, hasDisabledEnvironment, envValue]);

    useEffect(() => {
        if (form) {
            setProductList(null);
            setSubProductList(null);
            const { promise, cancel } = ProductAPI.getAllQuick();
            promise.then((responseProductsList) => {
                const allProductsList = [
                    {
                        ...ALL_PRODUCTS_CONFIG
                    },
                    ...responseProductsList,
                ];
                setProductList(allProductsList);
            }, _.noop);
            return cancel;
        }
    }, [form])

    useEffect(() => {
        if(isEdit){
            form.setFieldsValue({channelId: Object.keys(config?.channels)});
        }
    },[isEdit, config, form])

    useEffect(() => {
        setSubProductList(null);
        if (selectedProduct && form && selectedProduct !== 'All Products') {
            const { promise, cancel } = SubProductAPI.getAllQuick(selectedProduct);
            promise.then((responseSubProductsList) => {
                const allSubProductsList = [
                    {
                        ...ALL_SUBPRODUCTS_CONFIG
                    },
                    ...responseSubProductsList
                ];
                setSubProductList(allSubProductsList);
            }, _.noop);
            return cancel;
        } else if (selectedProduct === 'All Products') {
            setSubProductList([{
                ...ALL_SUBPRODUCTS_CONFIG
            }]);
            form.setFields([
                {
                    name: 'aspId',
                    value: null,
                },
                {
                    name: 'aeId',
                    value: null,
                },
            ]);
        }
    }, [form, selectedProduct]);

    const fetchChannels = useCallback(() => {
            const { promise, cancel } = ToolsIntegrationsAPI.getSlackChannels(workspaceId);
            promise.then((data = []) => {
                setChannels(data.map(d => ({ name: d.channelName, id: d.channelId })));
            }, console.log);
            promise.catch(() => {
                thisRef.current.close();
            });
            return cancel;
    }, [workspaceId]);


    // const appendAll = useCallback((list = []) => ([{ label: "All", value: "all" }, ...list]), []);

    useEffect(() => {
        return fetchChannels();
    }, [fetchChannels]);

    const fields = [
        {
            name: "apId",
            label: <Vocabulary name="product" />,
            type: FieldTypes.SELECT,
            labelAlign: "left",
            options: productList,
            required: true,
            props: {
                onChange: (value) => {
                    form.setFields([
                        {
                            name: 'aspId',
                            value: null,
                        },
                        {
                            name: 'aeId',
                            value: null,
                        },
                    ]);
                    setSelectedProduct(value);
                },
                autoFocus: true
                // optionsMutator: appendAll
            }
        }, {
            name: "aspId",
            label: <Vocabulary name="subProduct" />,
            type: FieldTypes.SELECT,
            labelAlign: "left",
            options: subProductList,
            props: {
                onChange: (value) => {
                    form.setFields([
                        {
                            name: 'aeId',
                            value: null,
                        },
                    ]);
                },
                allowClear: true,
                clearIcon: <Tooltip title={"Remove"}><CloseIcon /></Tooltip>
                // optionsMutator: appendAll
            }
        }, ...(hasDisabledEnvironment ? [] : [{
            name: "aeId",
            label: "Environment",
            labelAlign: "left",
            type: FieldTypes.SELECT,
            options: Environments,
            props: {
                allowClear: true,
                clearIcon: <Tooltip title={"Remove"}><CloseIcon /></Tooltip>
                // optionsMutator: appendAll
            }
        }]),
        {
            name: "channelId",
            label: "Channel Name",
            type: FieldTypes.SELECT,
            labelAlign: "left",
            options: channels,
            loading: _.isUndefined(channels),
            required: true,
            help: ChannelHelperText,
            props: {
                mode: 'tags',
            }
        },
    ];

    return <BaseModalForm
        ref={thisRef}
        title={isEdit ? "Edit Slack Mapping" : "Add Mapping"}
        initialValues={{
            ...config,
            aeId: config?.aeName,
            apId: config?.apId ?? ALL_PRODUCTS_CONFIG.id,
            aspId: config?.aspId ?? ALL_SUBPRODUCTS_CONFIG.id
        }}
        form={form}
        onOk={saveConfig}
        afterClose={afterClose}
        fields={fields}
    />
}
